import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Setup checklist for new businesses. Computes a percent-complete from the user/services
 * state and renders a progress card with a clickable checklist.
 */
export default function OnboardingChecklist({ user, services = [], onAddService, onEditProfile }) {
  const items = useMemo(() => {
    const subActive = !!user?.subscription_active;
    const profileImg = !!user?.profile_image_url;
    const description = (user?.business_description || "").trim().length >= 30;
    const phone = !!(user?.contact_phone || user?.phone);
    const stripeReady = !!user?.stripe_charges_enabled;
    const firstService = (services || []).length > 0;
    const availability = (services || []).some((s) => Array.isArray(s.availability) && s.availability.length > 0);
    const locationSet = !!(user?.address && user?.city);
    return [
      { key: "profile_img", label: "Add a profile photo / logo", done: profileImg, hint: "First impressions matter. A logo or storefront photo lifts conversion.", action: onEditProfile, ctaLabel: "Add photo" },
      { key: "description", label: "Add a business description (30+ characters)", done: description, hint: "Tell customers what you do in 1–2 sentences.", action: onEditProfile, ctaLabel: "Edit profile" },
      { key: "contact_phone", label: "Add a contact phone number", done: phone, hint: "We use this for booking notifications.", action: onEditProfile, ctaLabel: "Edit profile" },
      { key: "location", label: "Set your address / city", done: locationSet, hint: "Required for distance-based search results.", action: onEditProfile, ctaLabel: "Edit profile" },
      { key: "subscription", label: "Activate subscription (monthly or annual)", done: subActive, hint: "$19.99/mo (7-day free trial on Monthly) or $199.99/yr (billed right away, no trial).", to: "/business/subscribe", ctaLabel: "Subscribe" },
      { key: "stripe", label: "Complete payout setup", done: stripeReady, hint: "Confirm your identity and payout account so BookFreely can send you your earnings and show the Verified badge. Your published services can be booked before this is done — until then your share of each payment is held as a pending transfer.", to: "/business", ctaLabel: "Continue payout setup" },
      { key: "first_service", label: "Add your first service", done: firstService, hint: "Customers can only book once a service is listed.", action: onAddService, ctaLabel: "Add service" },
      { key: "availability", label: "Set availability for at least one service", done: availability, hint: "Hours when customers can book.", action: onAddService, ctaLabel: "Edit availability" },
    ];
  }, [user, services, onAddService, onEditProfile]);

  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / total) * 100);
  const [open, setOpen] = useState(pct < 100);

  if (pct === 100) {
    return (
      <div data-testid="onboarding-complete" className="mt-6 rounded-2xl border p-5 flex items-center justify-between flex-wrap gap-3"
        style={{ borderColor: "var(--gf-surface)", background: "var(--gf-surface)" }}>
        <div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-accent)" }}>Setup complete</div>
          <div className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>You're ready to book customers</div>
        </div>
        <Link to="/services" className="text-xs rounded-full px-4 py-2 font-medium" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
          See your live profile →
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="onboarding-checklist" className="mt-6 rounded-2xl border" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
      <button
        data-testid="onboarding-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-5"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Get set up</div>
            <div className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>
              Your business profile is <span style={{ color: "var(--gf-accent)" }}>{pct}%</span> complete
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
              {done} of {total} items done · {total - done} left to go
            </div>
          </div>
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
            {open ? "Hide" : "Show"}
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: "var(--gf-bg-soft)" }}>
          <div
            data-testid="onboarding-progress-bar"
            className="h-full transition-all"
            style={{ width: `${pct}%`, background: "var(--gf-accent)" }}
          />
        </div>
      </button>

      {open && (
        <ul className="divide-y" style={{ borderColor: "var(--gf-border)" }}>
          {items.map((item) => (
            <li
              key={item.key}
              data-testid={`onboarding-item-${item.key}`}
              className="px-5 py-3 flex items-start gap-3"
              style={{ borderTop: "1px solid var(--gf-border)" }}
            >
              <span
                className="mt-1 inline-flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: "18px",
                  height: "18px",
                  background: item.done ? "var(--gf-accent)" : "transparent",
                  border: item.done ? "none" : "1.5px solid var(--gf-border-strong)",
                  color: "var(--gf-on-accent)",
                  fontSize: "11px",
                  lineHeight: 1,
                }}
              >
                {item.done ? "✓" : ""}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm" style={{ color: item.done ? "var(--gf-text-muted)" : "var(--gf-text)", textDecoration: item.done ? "line-through" : "none" }}>
                  {item.label}
                </div>
                {!item.done && item.hint && (
                  <div className="text-xs mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{item.hint}</div>
                )}
              </div>
              {!item.done && !item.info && (item.to ? (
                <Link
                  to={item.to}
                  data-testid={`onboarding-cta-${item.key}`}
                  className="text-xs rounded-full px-3 py-1.5 whitespace-nowrap"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                >
                  {item.ctaLabel} →
                </Link>
              ) : (
                <button
                  data-testid={`onboarding-cta-${item.key}`}
                  onClick={item.action}
                  className="text-xs rounded-full px-3 py-1.5 whitespace-nowrap"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                >
                  {item.ctaLabel} →
                </button>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
